import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import './Dashboard.css';
import './CRM.css';
import { fetchFirstFnolJson } from '../utils/fnolApi';
import { openDocumentUploadBucket } from '../utils/documentUploadLink';

const FNOL_MODULE_AGENT_ID = 'insurance-fnol-claims-new';
const FNOL_GENERATED_EVENT = 'genpact:fnol-generated';
const FNOL_GENERATED_STORAGE_KEY = 'genpact.agentAssist.latestFnolGenerated';
const FNOL_REFRESH_INTERVAL_MS = 5000;

const displayValue = (value) => {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return value;
};

const firstValue = (data, keys) =>
  keys.map((key) => data?.[key]).find((value) => value !== undefined && value !== null && value !== '');

const getFieldDataValue = (data, key) => {
  if (typeof key === 'function') return key(data);
  if (Array.isArray(key)) return firstValue(data, key);
  return data?.[key];
};

const hasAnyValue = (data, keys) =>
  keys.some((key) => data?.[key] !== undefined && data?.[key] !== null && data?.[key] !== '');

const getFnolRecordId = (record = {}) =>
  firstValue(record, ['fnol_id', 'fnolId', 'fnol_number', 'fnolNumber', 'id', 'intake_id']);

const mailClaimIds = new Set(['C-260902-002', 'C-260902-001', 'C-260901-007']);
const callClaimIds = new Set(['C-260902-003', 'C-260831-006', 'C-260902-004']);

const deriveClaimIdFromFnolId = (recordId = '') => {
  const normalized = String(recordId || '').trim().toUpperCase();
  return normalized.startsWith('F-') ? `C-${normalized.slice(2)}` : normalized;
};

const isKnownMailRecord = (record = {}) => {
  const values = [
    getFnolRecordId(record),
    firstValue(record, ['claim_id', 'claimId', 'claimid']),
  ];
  return values.some((value) => mailClaimIds.has(deriveClaimIdFromFnolId(value)));
};

const isKnownCallRecord = (record = {}) => {
  const values = [
    getFnolRecordId(record),
    firstValue(record, ['claim_id', 'claimId', 'claimid']),
  ];
  return values.some((value) => callClaimIds.has(deriveClaimIdFromFnolId(value)));
};

const visibleFnolClaimIds = new Set([
  'C-260902-002',
  'C-260902-001',
  'C-260901-007',
  'C-260902-003',
  'C-260831-006',
  'C-260902-004',
  'C-260901-006',
]);

const isVisibleFnolRecord = (record = {}) => {
  const fnolId = getFnolRecordId(record);
  const claimId = firstValue(record, ['claim_id', 'claimId', 'claimid']);
  return [fnolId, claimId].some((value) =>
    visibleFnolClaimIds.has(deriveClaimIdFromFnolId(value))
  );
};

const normalizeLineOfBusiness = (value) => {
  const normalized = String(value || '').trim().toLowerCase().replace(/[_-]/g, ' ');
  if (['gl', 'general', 'general liability', 'liability'].includes(normalized)) {
    return 'general';
  }
  if (normalized.includes('property')) return 'property';
  if (normalized.includes('auto')) return 'auto';
  return normalized;
};

const formatIntakeValue = (ticket = {}) => {
  if (isKnownCallRecord(ticket)) return 'CALL';
  if (ticket.is_email_source || isKnownMailRecord(ticket)) return 'EMAIL';

  const interactionChannel = firstValue(ticket, [
    'interaction_channel',
    'interactionChannel',
    'Interaction_Channel',
  ]);

  if (interactionChannel) {
    return String(interactionChannel).trim().toUpperCase();
  }

  return firstValue(ticket, ['intake_id', 'intakeId']) || '-';
};

const fnolLineTabs = [
  { id: 'auto', label: 'Auto', icon: 'fa-car-side' },
  { id: 'property', label: 'Property', icon: 'fa-house-chimney' },
  { id: 'general', label: 'General Liability', icon: 'fa-scale-balanced' },
];

const getEditableFieldKey = (key, data = {}) => {
  if (typeof key === 'string') return key;
  if (Array.isArray(key)) {
    return (
      key.find((candidate) => data?.[candidate] !== undefined && data?.[candidate] !== null && data?.[candidate] !== '') ||
      key[0]
    );
  }
  return '';
};

const shouldUseTextarea = (label = '', value = '') =>
  /description|address|notes|information/i.test(label) || String(value || '').length > 80;

const getDraftFieldValue = (data, editValues, key, fallbackValue) => {
  if (key && Object.prototype.hasOwnProperty.call(editValues, key)) {
    return editValues[key];
  }
  return fallbackValue;
};

const IncidentField = ({
  label,
  value,
  showIfEmpty = true,
  fullWidth = false,
  editable = false,
  fieldKey = '',
  onChange,
}) => {
  const hasValue = value !== undefined && value !== null && value !== '';
  const displayText = hasValue ? displayValue(value) : '';
  const isBooleanControl =
    typeof value === 'boolean' ||
    /available|called|transport|involved|deployed|required|drivable/i.test(label);
  const booleanText = String(value || '').trim().toLowerCase();
  const editText = isBooleanControl
    ? hasValue
      ? ['yes', 'true', '1'].includes(booleanText)
        ? 'true'
        : 'false'
      : ''
    : String(displayText || '');

  return (
  <div className={`incident-field ${fullWidth ? 'incident-field-full' : ''}`}>
    {showIfEmpty || hasValue ? (
      <>
        <span className="incident-field-label">{label}</span>
        <span className="incident-separator">-</span>
        {editable && fieldKey ? (
          isBooleanControl ? (
            <select
              className="incident-edit-control"
              value={editText}
              onChange={(event) => onChange?.(fieldKey, event.target.value)}
            >
              <option value="">-</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          ) : shouldUseTextarea(label, displayText) ? (
            <textarea
              className="incident-edit-control incident-edit-textarea"
              value={editText}
              onChange={(event) => onChange?.(fieldKey, event.target.value)}
            />
          ) : (
            <input
              className="incident-edit-control"
              type="text"
              value={editText}
              onChange={(event) => onChange?.(fieldKey, event.target.value)}
            />
          )
        ) : (
          <strong>{displayText}</strong>
        )}
      </>
    ) : null}
  </div>
  );
};

const IncidentSection = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <section className={`incident-section ${isOpen ? '' : 'collapsed'}`}>
      <button
        type="button"
        className="incident-section-toggle"
        onClick={() => setIsOpen((current) => !current)}
        aria-expanded={isOpen}
      >
        <span>{title}</span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} aria-hidden="true"></i>
      </button>
      {isOpen && <div className="incident-section-body">{children}</div>}
    </section>
  );
};

const autoFieldKeys = [
  'vehicle_make',
  'vehicle_model',
  'vehicle_year',
  'vehicle_vin',
  'vehicle_plate',
  'driver_name',
  'driver_dob',
  'driver_license_number',
  'weather_conditions',
  'road_conditions',
  'vehicle_count',
  'police_called',
  'police_agency',
  'vehicle_drivable',
  'vehicle_location',
  'damage_description',
  'airbag_deployed',
  'rental_required',
  'other_driver_name',
  'other_driver_phone',
  'other_driver_insurance',
  'other_driver_policy_number',
  'other_driver_plate',
  'citations_issued',
  'witness_name',
  'witness_phone',
  'additional_notes',
];

const generalLiabilityFieldKeys = [
  'injured_party_name',
  'medical_treatment',
  'treatment_location',
  'ems_called',
  'ambulance_transport',
  'attorney_involved',
  'attorney_name',
  'incident_cause',
  'property_owner',
  'incident_report_available',
];

const propertyFieldKeys = [
  'intake_id',
  'property_address',
  'occupancy_status',
  'habitable_status',
  'suspected_cause',
  'affected_areas',
  'active_damage',
  'mitigation_actions',
  'photos_available',
  'other_insurance',
  'water_source',
  'flood_involved',
  'sewer_backup',
  'fire_department_response',
  'fire_report_number',
  'storm_event',
  'police_report_number',
  'created_at',
  'updated_at',
  'injury_information',
  'policy_number',
  'insured_name',
  'insured_dob',
  'marital_status',
  'gender',
  'address',
  'zip_code',
  'phone_primary',
  'phone_secondary',
  'email_id_primary',
  'email_id_secondary',
  'employer',
  'occupation',
  'insured_spouse_name',
  'insured_spouse_dob',
  'insured_spouse_marital_status',
  'insured_spouse_gender',
  'insured_spouse_email_address',
  'insured_spouse_address',
  'insured_spouse_zip_code',
  'date_of_loss',
  'reported_by',
  'loss_description',
  'fnol_id',
  'claim_id',
  'claim_status',
  'utilities_status',
  'temporary_housing_needed',
  'additional_notes',
  'fire_cause',
  'water_loss_type',
  'tree_owner',
  'damaged_property',
  'point_of_entry',
  'items_stolen_or_damaged',
  'alarm_status',
  'vacancy_duration',
  'interaction_channel',
];

const frontendReadonlyFnolFields = new Set([
  'insured',
  'insured_name',
  'policy_number',
  'claim_id',
  'fnol_id',
  'insured_dob',
  'marital_status',
  'gender',
  'address',
  'zip_code',
  'phone_primary',
  'primary_phone',
  'phone_secondary',
  'secondary_phone',
  'email_id_primary',
  'primary_email',
  'email_id_secondary',
  'secondary_email',
  'employer',
  'occupation',
  'insured_spouse_name',
  'insured_spouse_dob',
  'insured_spouse_marital_status',
  'insured_spouse_gender',
  'insured_spouse_email_address',
  'insured_spouse_address',
  'insured_spouse_zip_code',
  'date_of_loss',
  'reported_by',
  'interaction_channel',
  'created_at',
  'updated_at',
]);

const propertyBackendSections = [
  {
    title: 'General Information',
    fields: [
      ['Policy #', 'policy_number'],
      ['Insured Name', ['insured_name', 'insured']],
      ['Insured DOB', 'insured_dob'],
      ['Marital Status', 'marital_status'],
      ['Gender', 'gender'],
      ['Address', 'address'],
      ['Zip Code', 'zip_code'],
      ['Primary Phone', ['phone_primary', 'primary_phone']],
      ['Secondary Phone', ['phone_secondary', 'secondary_phone']],
      ['Primary Email ID', ['email_id_primary', 'primary_email']],
      ['Secondary Email ID', ['email_id_secondary', 'secondary_email']],
      ['Employer', 'employer'],
      ['Occupation', 'occupation'],
      ['Insured Spouse Name', 'insured_spouse_name'],
      ['Insured Spouse DOB', 'insured_spouse_dob'],
      ['Insured Spouse Marital Status', 'insured_spouse_marital_status'],
      ['Insured Spouse Gender', 'insured_spouse_gender'],
      ['Insured Spouse Email Address', 'insured_spouse_email_address'],
      ['Insured Spouse Address', 'insured_spouse_address'],
      ['Insured Spouse Zip Code', 'insured_spouse_zip_code'],
      ['Date of Loss', 'date_of_loss'],
      ['Reported By', 'reported_by'],
      ['Interaction Channel', 'interaction_channel'],
    ],
  },
  {
    title: 'Property Information',
    fields: [
      ['Intake ID', 'intake_id'],
      ['Property Address', 'property_address'],
      ['Occupancy Status', 'occupancy_status'],
      ['Habitable Status', 'habitable_status'],
    ],
  },
  {
    title: 'Property Damage',
    fields: [
      ['Suspected Cause', 'suspected_cause'],
      ['Affected Areas', 'affected_areas'],
      ['Active Damage', 'active_damage'],
      ['Mitigation Actions', 'mitigation_actions'],
      ['Photos Available', 'photos_available'],
      ['Other Insurance', 'other_insurance'],
    ],
  },
  {
    title: 'Property Cause and Reports',
    fields: [
      ['Water Source', 'water_source'],
      ['Flood Involved', 'flood_involved'],
      ['Sewer Backup', 'sewer_backup'],
      ['Fire Department Response', 'fire_department_response'],
      ['Fire Report #', 'fire_report_number'],
      ['Storm Event', 'storm_event'],
      ['Police Report #', 'police_report_number'],
    ],
  },
  {
    title: 'System Dates',
    fields: [
      ['Created At', 'created_at'],
      ['Updated At', 'updated_at'],
    ],
  },
  {
    title: 'Injury Information',
    fields: [
      ['Injury Information', 'injury_information'],
    ],
  },
  {
    title: 'Loss Description',
    fields: [
      ['Description', 'loss_description'],
    ],
  },
  {
    title: 'Claim Details',
    fields: [
      ['FNOL ID', 'fnol_id'],
      ['Claim ID', 'claim_id'],
      ['Claim Status', 'claim_status'],
    ],
  },
  {
    title: 'Additional Property Details',
    fields: [
      ['Utilities Status', 'utilities_status'],
      ['Temporary Housing Needed', 'temporary_housing_needed'],
      ['Additional Notes', 'additional_notes'],
      ['Fire Cause', 'fire_cause'],
      ['Water Loss Type', 'water_loss_type'],
      ['Tree Owner', 'tree_owner'],
      ['Damaged Property', 'damaged_property'],
      ['Point of Entry', 'point_of_entry'],
      ['Items Stolen or Damaged', 'items_stolen_or_damaged'],
      ['Alarm Status', 'alarm_status'],
      ['Vacancy Duration', 'vacancy_duration'],
      ['Interaction Channel', 'interaction_channel'],
    ],
  },
];

const renderIncidentFields = (
  fields,
  data,
  { editable = false, editValues = {}, onFieldChange } = {}
) =>
  fields.map(([label, key]) => {
    const fieldKey = getEditableFieldKey(key, data);
    const value = getDraftFieldValue(data, editValues, fieldKey, getFieldDataValue(data, key));
    const canEditField = editable && fieldKey && !frontendReadonlyFnolFields.has(fieldKey);

    return (
      <IncidentField
        key={`${label}-${Array.isArray(key) ? key.join('-') : key}`}
        label={label}
        value={value}
        showIfEmpty={Boolean(Object.keys(data).length)}
        editable={canEditField}
        fieldKey={fieldKey}
        onChange={onFieldChange}
      />
    );
  });

const enrichTicketsWithInteractionChannels = async (records = [], expectedLineTab = '') => {
  const expectedLob = normalizeLineOfBusiness(expectedLineTab);
  const enrichedRecords = await Promise.all(
    records.map(async (record) => {
      const recordId = getFnolRecordId(record);
      const recordLob = normalizeLineOfBusiness(
        firstValue(record, ['line_of_business', 'lob', 'loss_type', 'type_of_loss'])
      );

      if (!recordId) {
        return expectedLob ? null : record;
      }

      if (expectedLob && recordLob && recordLob !== expectedLob) {
        return null;
      }

      try {
        const detail = await fetchFirstFnolJson(
          `/fnol-records/${encodeURIComponent(recordId)}`,
          { timeoutMs: 8000 }
        );
        const detailData = detail.form_data || detail.automation || {};
        const detailLob = normalizeLineOfBusiness(
          firstValue(detailData, ['line_of_business', 'lob', 'loss_type', 'type_of_loss'])
        );

        if (expectedLob && !recordLob && detailLob && detailLob !== expectedLob) {
          return null;
        }

        const interactionChannel = firstValue(detailData, [
          'interaction_channel',
          'interactionChannel',
          'Interaction_Channel',
        ]);

        const isCallSource =
          isKnownCallRecord(record) ||
          isKnownCallRecord(detailData);
        const isEmailSource =
          !isCallSource &&
          (
            isKnownMailRecord(record) ||
            isKnownMailRecord(detailData) ||
            record.is_email_source ||
            detailData.is_email_source ||
            String(record.interaction_channel || '').trim().toUpperCase() === 'EMAIL' ||
            String(interactionChannel || '').trim().toUpperCase() === 'EMAIL'
          );

        return {
          ...record,
          line_of_business: recordLob || detailLob || record.line_of_business,
          lob: recordLob || detailLob || record.lob,
          is_email_source: isEmailSource && !isCallSource,
          interaction_channel: isCallSource
            ? 'CALL'
            : isEmailSource
            ? 'EMAIL'
            : interactionChannel || record.interaction_channel,
        };
      } catch {
        return record;
      }
    })
  );

  return enrichedRecords.filter(Boolean);
};

const ClaimForm = ({
  data = {},
  activeLineTab = '',
  isEditing = false,
  editValues = {},
  onEdit,
  onCancel,
  onSave,
  onFieldChange,
  isSaving = false,
  saveMessage = '',
  saveError = '',
  showEditBar = true,
}) => {
  const shouldInferLineFields = !activeLineTab;
  const showAutoFields =
    activeLineTab === 'auto' || (shouldInferLineFields && hasAnyValue(data, autoFieldKeys));
  const showGeneralLiabilityFields =
    activeLineTab === 'general' ||
    (shouldInferLineFields && hasAnyValue(data, generalLiabilityFieldKeys));
  const showPropertyFields =
    activeLineTab === 'property' || (shouldInferLineFields && hasAnyValue(data, propertyFieldKeys));
  const hasData = Boolean(Object.keys(data).length);
  const editableField = (label, key, options = {}) => {
    const fieldKey = options.fieldKey || getEditableFieldKey(key, data);
    return (
      <IncidentField
        label={label}
        value={getDraftFieldValue(data, editValues, fieldKey, getFieldDataValue(data, key))}
        showIfEmpty={options.showIfEmpty ?? hasData}
        fullWidth={options.fullWidth}
        editable={isEditing && fieldKey && !frontendReadonlyFnolFields.has(fieldKey)}
        fieldKey={fieldKey}
        onChange={onFieldChange}
      />
    );
  };
  const renderEditBar = () => (
    <div className="fnol-edit-bar">
      <div className="fnol-edit-status" aria-live="polite">
        {saveError ? <span className="fnol-save-error">{saveError}</span> : saveMessage}
      </div>
      <div className="fnol-edit-actions">
        {isEditing ? (
          <>
            <button type="button" className="fnol-edit-button secondary" onClick={onCancel} disabled={isSaving}>
              Cancel
            </button>
            <button type="button" className="fnol-edit-button" onClick={onSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </>
        ) : (
          <button type="button" className="fnol-edit-button" onClick={onEdit} disabled={!hasData}>
            Edit
          </button>
        )}
      </div>
    </div>
  );

  if (activeLineTab === 'property') {
    return (
      <div className="incident-report">
        {showEditBar && renderEditBar()}
        {propertyBackendSections.map((section) => (
          <IncidentSection title={section.title} key={section.title}>
            {renderIncidentFields(section.fields, data, {
              editable: isEditing && section.title !== 'General Information',
              editValues,
              onFieldChange,
            })}
          </IncidentSection>
        ))}
      </div>
    );
  }

  return (
  <div className="incident-report">
    {showEditBar && renderEditBar()}
    <IncidentSection title="General Information">
      <IncidentField label="Insured" value={firstValue(data, ['insured', 'insured_name', 'insured_full_name', 'customer_name'])} />
      <IncidentField label="Policy #" value={data.policy_number} />
      <IncidentField label="Claim ID" value={firstValue(data, ['claim_id', 'claimId', 'claimid', 'claim_number', 'claimNumber'])} />
      <IncidentField label="Insured DOB" value={data.insured_dob} />
      <IncidentField label="Marital Status" value={data.marital_status} />
      <IncidentField label="Gender" value={data.gender} />
      <IncidentField label="Address" value={firstValue(data, ['address', 'insured_address', 'claimant_address'])} />
      <IncidentField label="Zip Code" value={data.zip_code} />
      <IncidentField label="Primary Phone" value={firstValue(data, ['primary_phone', 'phone_primary', 'insured_phone'])} />
      <IncidentField label="Secondary Phone" value={firstValue(data, ['secondary_phone', 'phone_secondary'])} />
      <IncidentField label="Primary Email ID" value={firstValue(data, ['primary_email', 'email_id_primary', 'insured_email', 'sender_email'])} />
      <IncidentField label="Secondary Email ID" value={firstValue(data, ['secondary_email', 'email_id_secondary'])} />
      <IncidentField label="Employer" value={data.employer} />
      <IncidentField label="Occupation" value={data.occupation} />
      <IncidentField label="Insured Spouse Name" value={data.insured_spouse_name} />
      <IncidentField label="Insured Spouse DOB" value={data.insured_spouse_dob} />
      <IncidentField label="Insured Spouse Marital Status" value={data.insured_spouse_marital_status} />
      <IncidentField label="Insured Spouse Gender" value={data.insured_spouse_gender} />
      <IncidentField label="Insured Spouse Email Address" value={data.insured_spouse_email_address} />
      <IncidentField label="Insured Spouse Address" value={data.insured_spouse_address} />
      <IncidentField label="Insured Spouse Zip Code" value={data.insured_spouse_zip_code} />
      <IncidentField label="Date of Loss" value={firstValue(data, ['date_of_loss', 'date_reported', 'created_timestamp'])} />
      <IncidentField label="Reported By" value={firstValue(data, ['reported_by', 'insured_name', 'insured', 'sender_email'])} />
      <IncidentField
        label="Interaction Channel"
        value={firstValue(data, ['interaction_channel', 'interactionChannel', 'Interaction_Channel'])}
      />
    </IncidentSection>

    <IncidentSection title="Claimant Information">
      {editableField('Name', ['claimant_name', 'claimantName', 'claimant', 'injured_party_name', 'reported_by', 'insured', 'insured_name', 'insured_full_name', 'customer_name'], { fieldKey: 'claimant_name' })}
      {editableField('Date of Birth', ['claimant_dob', 'claimantDOB', 'claimant_date_of_birth', 'insured_dob'], { fieldKey: 'claimant_dob' })}
      {editableField('Address', ['claimant_address', 'claimantAddress', 'address', 'insured_address'], { fieldKey: 'claimant_address' })}
      {editableField('Phone', ['claimant_phone', 'claimantPhone', 'claimant_primary_phone', 'primary_phone', 'phone_primary', 'insured_phone'], { fieldKey: 'claimant_phone' })}
      {editableField('Email', ['claimant_email', 'claimantEmail', 'claimant_primary_email', 'primary_email', 'email_id_primary', 'insured_email', 'sender_email'], { fieldKey: 'claimant_email' })}
    </IncidentSection>

    <div className="incident-standalone-field">
      <div className="standalone-field-heading">Loss Type</div>
      {editableField('Loss Type', ['loss_type', 'type_of_loss', 'lossType', 'possible_loss_type', 'loss_category', 'category'], { fieldKey: 'loss_type' })}
    </div>

    {showGeneralLiabilityFields && (
      <IncidentSection title="General Liability Incident">
        {editableField('Intake ID', 'intake_id')}
        {editableField('Incident Cause', 'incident_cause')}
        {editableField('Property Owner', 'property_owner')}
        {editableField('Photos Available', 'photos_available')}
        {editableField('Incident Report Available', 'incident_report_available')}
      </IncidentSection>
    )}

    {showPropertyFields && (
      <>
        <IncidentSection title="Property Information">
          {editableField('Intake ID', 'intake_id')}
          {editableField('Property Address', 'property_address')}
          {editableField('Occupancy Status', 'occupancy_status')}
          {editableField('Habitable Status', 'habitable_status')}
          {editableField('Other Insurance', 'other_insurance')}
        </IncidentSection>

        <IncidentSection title="Property Damage">
          {editableField('Suspected Cause', 'suspected_cause')}
          {editableField('Affected Areas', 'affected_areas')}
          {editableField('Active Damage', 'active_damage')}
          {editableField('Mitigation Actions', 'mitigation_actions')}
          {editableField('Photos Available', 'photos_available')}
        </IncidentSection>

        <IncidentSection title="Property Cause and Reports">
          {editableField('Water Source', 'water_source')}
          {editableField('Flood Involved', 'flood_involved')}
          {editableField('Sewer Backup', 'sewer_backup')}
          {editableField('Fire Department Response', 'fire_department_response')}
          {editableField('Fire Report #', 'fire_report_number')}
          {editableField('Storm Event', 'storm_event')}
          {editableField('Police Report #', 'police_report_number')}
        </IncidentSection>
      </>
    )}

    {showAutoFields && (
      <>
        <IncidentSection title="Auto Vehicle Information">
          {editableField('Vehicle Make', 'vehicle_make')}
          {editableField('Vehicle Model', 'vehicle_model')}
          {editableField('Vehicle Year', 'vehicle_year')}
          {editableField('VIN', 'vehicle_vin')}
          {editableField('Plate', 'vehicle_plate')}
          {editableField('Vehicle Count', 'vehicle_count')}
          {editableField('Vehicle Drivable', 'vehicle_drivable')}
          {editableField('Vehicle Location', 'vehicle_location')}
        </IncidentSection>

        <IncidentSection title="Driver Information">
          {editableField('Driver Name', 'driver_name')}
          {editableField('Driver DOB', 'driver_dob')}
          {editableField('Driver License #', 'driver_license_number')}
          {editableField('Other Driver Name', 'other_driver_name')}
          {editableField('Other Driver Phone', 'other_driver_phone')}
          {editableField('Other Driver Insurance', 'other_driver_insurance')}
          {editableField('Other Driver Policy #', 'other_driver_policy_number')}
          {editableField('Other Driver Plate', 'other_driver_plate')}
        </IncidentSection>

        <IncidentSection title="Accident Conditions">
          {editableField('Weather Conditions', 'weather_conditions')}
          {editableField('Road Conditions', 'road_conditions')}
          {editableField('Police Called', 'police_called')}
          {editableField('Police Report #', 'police_report_number')}
          {editableField('Police Agency', 'police_agency')}
          {editableField('Citations Issued', 'citations_issued')}
        </IncidentSection>

        <IncidentSection title="Vehicle Damage">
          {editableField('Damage Description', 'damage_description')}
          {editableField('Airbag Deployed', 'airbag_deployed')}
          {editableField('Photos Available', 'photos_available')}
          {editableField('Rental Required', 'rental_required')}
          {editableField('Injury Information', 'injury_information')}
          {editableField('Additional Notes', 'additional_notes')}
        </IncidentSection>
      </>
    )}

    <IncidentSection title="Loss Description">
      {editableField('Description', 'loss_description', { fullWidth: true })}
      {editableField('Location', 'loss_location', { fullWidth: true })}
    </IncidentSection>

    <IncidentSection title="Injury Information">
      {editableField('Complaint', ['injury_complaint', 'injury_information'], { fieldKey: 'injury_information' })}
      {editableField('Medical Treatment', ['medical_treatment', 'medical_care_onsite'], { fieldKey: 'medical_treatment' })}
      {editableField('Treatment Location', 'treatment_location')}
      {editableField('EMS Called', 'ems_called')}
      {editableField('Transport', ['ambulance_transport', 'hospital_transport'], { fieldKey: 'ambulance_transport' })}
    </IncidentSection>

    <IncidentSection title="Attorney">
      {editableField('Attorney Involved', 'attorney_involved')}
      {editableField('Attorney', ['attorney', 'attorney_name'], { fieldKey: 'attorney_name' })}
    </IncidentSection>

    <IncidentSection title="Witness">
      {editableField('Witness 1 Name', ['witness_1_name', 'witness_name'], { fieldKey: 'witness_1_name' })}
      {editableField('Witness 1 Phone', ['witness_1_phone', 'witness_phone'], { fieldKey: 'witness_1_phone' })}
      {editableField('Witness 1 Address', 'witness_1_address')}
      {editableField('Witness 2 Name', 'witness_2_name', { showIfEmpty: hasAnyValue(data, ['witness_2_name']) || isEditing })}
      {editableField('Witness 2 Phone', 'witness_2_phone', { showIfEmpty: hasAnyValue(data, ['witness_2_phone']) || isEditing })}
      {editableField('Witness 2 Address', 'witness_2_address', { showIfEmpty: hasAnyValue(data, ['witness_2_address']) || isEditing })}
    </IncidentSection>
  </div>
  );
};

const CRM = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tickets, setTickets] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState('');
  const [selectedFnol, setSelectedFnol] = useState({});
  const [isEditingFnol, setIsEditingFnol] = useState(false);
  const [fnolDraft, setFnolDraft] = useState({});
  const [isSavingFnol, setIsSavingFnol] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');
  const [loadError, setLoadError] = useState('');
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isFnolModule = searchParams.get('module') === 'fnol';
  const activeLineTab = fnolLineTabs.some((tab) => tab.id === searchParams.get('lob'))
    ? searchParams.get('lob')
    : '';
  const filteredTickets = tickets.filter(isVisibleFnolRecord);

  useEffect(() => {
    setIsEditingFnol(false);
    setFnolDraft({});
    setSaveMessage('');
    setSaveError('');
  }, [selectedTicketId]);

  const navigateFnolLine = (lineId = '') => {
    const params = new URLSearchParams();
    if (isFnolModule) params.set('module', 'fnol');
    if (lineId) params.set('lob', lineId);
    const query = params.toString();
    navigate(`/crm${query ? `?${query}` : ''}`);
  };

  useEffect(() => {
    let active = true;

    const loadTickets = async () => {
      try {
        const query = activeLineTab
          ? `?lob=${encodeURIComponent(activeLineTab)}`
          : '';
        const result = await fetchFirstFnolJson(`/fnol-records${query}`);
        const records = await enrichTicketsWithInteractionChannels(
          (result.records || []).filter(isVisibleFnolRecord),
          activeLineTab
        );
        if (active) {
          setTickets(records);
          setLoadError('');
        }
      } catch (error) {
        if (active) setLoadError(error.message);
      }
    };

    loadTickets();
    const refresh = window.setInterval(loadTickets, FNOL_REFRESH_INTERVAL_MS);
    const refreshOnGeneratedFnol = () => loadTickets();
    const refreshOnStorage = (event) => {
      if (event.key === FNOL_GENERATED_STORAGE_KEY) {
        loadTickets();
      }
    };
    const refreshOnVisible = () => {
      if (!document.hidden) {
        loadTickets();
      }
    };

    window.addEventListener(FNOL_GENERATED_EVENT, refreshOnGeneratedFnol);
    window.addEventListener('storage', refreshOnStorage);
    window.addEventListener('focus', refreshOnGeneratedFnol);
    document.addEventListener('visibilitychange', refreshOnVisible);

    return () => {
      active = false;
      window.clearInterval(refresh);
      window.removeEventListener(FNOL_GENERATED_EVENT, refreshOnGeneratedFnol);
      window.removeEventListener('storage', refreshOnStorage);
      window.removeEventListener('focus', refreshOnGeneratedFnol);
      document.removeEventListener('visibilitychange', refreshOnVisible);
    };
  }, [activeLineTab]);

  useEffect(() => {
    if (!filteredTickets.length) {
      setSelectedTicketId('');
      setSelectedFnol({});
      return;
    }

    if (selectedTicketId && !filteredTickets.some((ticket) => getFnolRecordId(ticket) === selectedTicketId)) {
      setSelectedTicketId('');
      setSelectedFnol({});
      return;
    }

    if (!selectedTicketId) {
      const firstTicketId = getFnolRecordId(filteredTickets[0]);
      setSelectedTicketId(firstTicketId || '');
      setSelectedFnol(filteredTickets[0] || {});
    }
  }, [filteredTickets, selectedTicketId]);

  useEffect(() => {
    if (!selectedTicketId) return;
    let active = true;

    const loadSelectedFnol = async () => {
      try {
        const result = await fetchFirstFnolJson(
          `/fnol-records/${encodeURIComponent(selectedTicketId)}`
        );
        if (active) {
          setSelectedFnol((current) => ({
            ...current,
            ...(result.form_data || {}),
          }));
          setLoadError('');
        }
      } catch (error) {
        if (active) setLoadError(error.message);
      }
    };

    loadSelectedFnol();
    return () => {
      active = false;
    };
  }, [selectedTicketId]);

  const handleStartEdit = () => {
    if (!Object.keys(selectedFnol).length) return;
    setFnolDraft({ ...selectedFnol });
    setSaveMessage('');
    setSaveError('');
    setIsEditingFnol(true);
  };

  const handleCancelEdit = () => {
    setFnolDraft({});
    setSaveMessage('');
    setSaveError('');
    setIsEditingFnol(false);
  };

  const handleFieldChange = (fieldKey, value) => {
    setFnolDraft((current) => ({
      ...current,
      [fieldKey]: value,
    }));
  };

  const handleSaveEdit = async () => {
    if (!selectedTicketId) return;

    const changedFields = Object.entries(fnolDraft).reduce((updates, [key, value]) => {
      if (frontendReadonlyFnolFields.has(key)) return updates;
      const originalValue = selectedFnol[key];
      const normalizedOriginal = originalValue === undefined || originalValue === null ? '' : String(originalValue);
      const normalizedNext = value === undefined || value === null ? '' : String(value);

      if (normalizedOriginal !== normalizedNext) {
        updates[key] = value;
      }
      return updates;
    }, {});

    if (!Object.keys(changedFields).length) {
      setSaveMessage('No changes to save.');
      setSaveError('');
      setIsEditingFnol(false);
      return;
    }

    setIsSavingFnol(true);
    setSaveMessage('');
    setSaveError('');

    try {
      const result = await fetchFirstFnolJson(
        `/fnol-records/${encodeURIComponent(selectedTicketId)}/update`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fields: changedFields }),
          timeoutMs: 15000,
        }
      );
      const updatedData = {
        ...selectedFnol,
        ...changedFields,
        ...(result.form_data || {}),
      };

      setSelectedFnol(updatedData);
      setTickets((currentTickets) =>
        currentTickets.map((ticket) =>
          getFnolRecordId(ticket) === selectedTicketId ? { ...ticket, ...updatedData } : ticket
        )
      );
      setFnolDraft({});
      setIsEditingFnol(false);
      setSaveMessage('Saved.');
    } catch (error) {
      setSaveError(error.message || 'Unable to save changes.');
    } finally {
      setIsSavingFnol(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return '';
    return new Date(value).toLocaleDateString([], {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getGeneratedDateFromRecordId = (ticket = {}) => {
    const recordId = getFnolRecordId(ticket);
    const match = String(recordId || '').match(/[FC]-(\d{2})(\d{2})(\d{2})-/i);
    if (!match) return '';

    const [, year, month, day] = match;
    return formatDate(`20${year}-${month}-${day}T00:00:00`);
  };

  const formatTicketDate = (ticket = {}) =>
    getGeneratedDateFromRecordId(ticket) || formatDate(ticket.processed_at);

  const openFnolReviewWindow = (ticketId) => {
    if (!ticketId) return;
    const origin =
      window.location.hostname === 'localhost' && window.location.port === '3100'
        ? 'http://127.0.0.1:3100'
        : window.location.origin;

    window.open(
      `${origin}/?fnolReview=${encodeURIComponent(ticketId)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };
  return (
    <div className="dashboard-page fnol-page">
      <Header onLogout={onLogout} />

      <div className="page-body">
        <aside className="sidebar mini-sidebar">
          <nav className="sidebar-nav">
            {isFnolModule ? (
              <>
                <button onClick={() => navigate('/home')} className="nav-item">
                  <i className="fas fa-home"></i><span className="nav-label">Home</span>
                </button>
                <button onClick={() => navigate('/crm?module=fnol')} className="nav-item active">
                  <i className="fas fa-address-book"></i><span className="nav-label">FNOL</span>
                </button>
                <div className="fnol-sub-tabs" aria-label="FNOL line of business tabs">
                  {fnolLineTabs.map((tab) => (
                    <button
                      type="button"
                      key={tab.id}
                      className={`fnol-sub-tab ${activeLineTab === tab.id ? 'active' : ''}`}
                      onClick={() => navigateFnolLine(tab.id)}
                    >
                      <i className={`fas ${tab.icon}`} aria-hidden="true"></i>
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => navigate(`/ai-agents?module=fnol&agentId=${FNOL_MODULE_AGENT_ID}`, { state: { fromFnolModule: true } })}
                  className="nav-item"
                >
                  <i className="fas fa-robot"></i><span className="nav-label">AI Agents</span>
                </button>
              </>
            ) : (
              <>
                <button onClick={() => navigate('/home')} className="nav-item">
                  <i className="fas fa-home"></i><span className="nav-label">Home</span>
                </button>
                <button onClick={() => navigate('/my-work')} className="nav-item">
                  <i className="fas fa-briefcase"></i><span className="nav-label">My Work</span>
                </button>
                <button onClick={() => navigate('/crm')} className="nav-item active">
                  <i className="fas fa-address-book"></i><span className="nav-label">FNOL</span>
                </button>
                <div className="fnol-sub-tabs" aria-label="FNOL line of business tabs">
                  {fnolLineTabs.map((tab) => (
                    <button
                      type="button"
                      key={tab.id}
                      className={`fnol-sub-tab ${activeLineTab === tab.id ? 'active' : ''}`}
                      onClick={() => navigateFnolLine(tab.id)}
                    >
                      <i className={`fas ${tab.icon}`} aria-hidden="true"></i>
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </div>
                <button onClick={() => navigate('/claims')} className="nav-item">
                  <i className="fas fa-file-shield"></i><span className="nav-label">Claim</span>
                </button>
                <button onClick={() => navigate('/ops-dashboard')} className="nav-item">
                  <i className="fas fa-tachometer-alt"></i><span className="nav-label">Ops Dashboard</span>
                </button>
                <button onClick={() => navigate('/claims?tab=quality-score')} className="nav-item quality-score-nav-item">
                  <i className="fas fa-clipboard-check"></i><span className="nav-label">QA</span>
                </button>
                <button onClick={() => navigate('/agent-dashboard')} className="nav-item">
                  <i className="fas fa-gauge-high"></i><span className="nav-label">AI Agent Dashboard</span>
                </button>
                <button onClick={openDocumentUploadBucket} className="nav-item">
                  <i className="fas fa-file-arrow-up"></i><span className="nav-label">Document Upload</span>
                </button>
              </>
            )}
          </nav>
          <div className="sidebar-footer">
            <button onClick={onLogout} className="nav-item logout-btn">
              <i className="fas fa-sign-out-alt"></i><span className="nav-label">Logout</span>
            </button>
          </div>
        </aside>

        <main className="db-content crm-content">
          <div className="crm-split-layout">
            <section className="crm-table-shell" aria-label="Claims">
              <table className="crm-ticket-table">
                <thead>
                  <tr>
                    <th>FNOL ID</th>
                    <th>Date</th>
                    <th>Intake</th>
                    <th>Customer Name</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTickets.length > 0 ? (
                    filteredTickets.map((ticket, index) => {
                      const ticketId = getFnolRecordId(ticket);

                      return (
                      <tr
                        key={ticketId || index}
                        className={selectedTicketId === ticketId ? 'selected-ticket-row' : ''}
                        onClick={() => {
                          if (!ticketId) return;
                          setSelectedTicketId(ticketId);
                          setSelectedFnol(ticket);
                        }}
                      >
                        <td className="ticket-number">
                          <button
                            type="button"
                            className="fnol-id-link"
                            onClick={(event) => {
                              event.stopPropagation();
                              openFnolReviewWindow(ticketId);
                            }}
                          >
                            {ticketId}
                          </button>
                        </td>
                        <td>{formatTicketDate(ticket)}</td>
                        <td>{formatIntakeValue(ticket)}</td>
                        <td>{ticket.customer_name}</td>
                      </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td className="empty-ticket-row" colSpan="4">
                        {loadError ||
                          (activeLineTab
                            ? `No ${fnolLineTabs.find((tab) => tab.id === activeLineTab)?.label} FNOL records yet.`
                            : 'No FNOL records yet.')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </section>

            <section className="crm-detail-pane" aria-label="Claim details">
              <ClaimForm
                data={selectedFnol}
                activeLineTab={activeLineTab}
                isEditing={isEditingFnol}
                editValues={fnolDraft}
                onEdit={handleStartEdit}
                onCancel={handleCancelEdit}
                onSave={handleSaveEdit}
                onFieldChange={handleFieldChange}
                isSaving={isSavingFnol}
                saveMessage={saveMessage}
                saveError={saveError}
              />
            </section>
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
};

export { ClaimForm, normalizeLineOfBusiness };
export default CRM;

